import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import {
  ActionConfirmationModal,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormSubmitButton,
  MultiselectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import {
  useCreateMatterScheduledMessage,
  useDeleteMatterScheduledMessage,
  useUpdateMatterScheduledMessage,
} from "@/lib/data/react-query/matters/matter-scheduled-message";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  buildMatterStatusGroups,
  CASE_MATTER_STATUS_CATEGORY_OPTIONS,
} from "@/lib/data/static/matterStatics";
import { type MatterScheduledMessage } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditMatterScheduledMessageModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface ConditionalProps {
  editing?: boolean;
  activeMatterScheduledMessage?: MatterScheduledMessage | null;
  isReadOnly?: boolean;
}

const formSchema = z.object({
  active: z.boolean(),
  content: z.string().trim().min(1, { message: "Content is required" }),
  description: z.string().nullable().optional(),
  label: z.string().trim().min(1, { message: "Label is required" }),
  incidentTypeIds: z
    .array(z.string())
    .min(1, { message: "Incident type is required" }),
  timezone: z.string().nullable().optional(),
  triggerStatusId: z.string().min(1, { message: "Matter status is required" }),
});
type FormValues = z.infer<typeof formSchema>;

const defaultFormValues = {
  label: "",
  content: "",
  description: "",
  active: true,
  incidentTypeIds: [],
  timezone: undefined,
  triggerStatusId: "",
};

const FORM_ID = "matter-scheduled-message-form";

export const AddOrEditMatterScheduledMessageModal = ({
  open,
  setOpen,
  editing,
  activeMatterScheduledMessage,
  isReadOnly,
}: AddOrEditMatterScheduledMessageModalProps & ConditionalProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMatterStatusOpen, setIsMatterStatusOpen] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...activeMatterScheduledMessage,
          incidentTypeIds: activeMatterScheduledMessage?.incidentTypeIds ?? [],
          triggerStatusId: activeMatterScheduledMessage?.triggerStatusId ?? "",
          active: activeMatterScheduledMessage?.active ?? true,
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const incidentTypes = matterSettingsData?.data.incidentTypes ?? [];
  const matterStatusGroups = useMemo(
    () =>
      buildMatterStatusGroups(
        (matterSettingsData?.data.statuses ?? []).filter((status) =>
          CASE_MATTER_STATUS_CATEGORY_OPTIONS.some(
            (category) => category === status.categoryId,
          ),
        ),
      ),
    [matterSettingsData?.data.statuses],
  );
  const matterScheduledMessages = useMemo(
    () => matterSettingsData?.data.scheduledMessages ?? [],
    [matterSettingsData],
  );

  const createMatterScheduledMessage = useCreateMatterScheduledMessage();
  const updateMatterScheduledMessage = useUpdateMatterScheduledMessage();
  const deleteMatterScheduledMessage = useDeleteMatterScheduledMessage();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const isFormLoading =
    createMatterScheduledMessage.isPending ||
    updateMatterScheduledMessage.isPending ||
    deleteMatterScheduledMessage.isPending;

  const onSubmit = async (data: FormValues) => {
    if (
      matterScheduledMessages.some(
        (message) =>
          toLower(message.label) === toLower(data.label) &&
          message.id !== activeMatterScheduledMessage?.id,
      )
    ) {
      form.setError("label", {
        message: "Scheduled message label must be unique",
      });
      return;
    }
    if (editing && activeMatterScheduledMessage) {
      await updateMatterScheduledMessage.mutateAsync({
        ...data,
        id: activeMatterScheduledMessage.id,
        dateModified: activeMatterScheduledMessage.dateModified,
        timezone: timezone,
      });
    } else {
      await createMatterScheduledMessage.mutateAsync({
        ...data,
        timezone: timezone,
      });
    }
    setOpen(false);
  };

  const handleDelete = async () => {
    if (activeMatterScheduledMessage?.id) {
      await deleteMatterScheduledMessage.mutateAsync({
        id: activeMatterScheduledMessage.id,
      });
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const deleteButton =
    !isReadOnly && editing ? (
      <div>
        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            setIsDeleting(true);
          }}
        >
          Delete
        </Button>
      </div>
    ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={
            editing
              ? `Edit scheduled message: ${activeMatterScheduledMessage?.label}`
              : "Create scheduled message"
          }
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && (
                  <FormSubmitButton
                    submitFormId={FORM_ID}
                    loading={
                      createMatterScheduledMessage.isPending ||
                      updateMatterScheduledMessage.isPending
                    }
                  />
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  label="Label*"
                  register={form.register("label")}
                />
                <SwitchFormField
                  label="Active"
                  register={form.register("active")}
                />
                <div className="col-span-full">
                  <TextAreaFormField
                    label="Description"
                    register={form.register("description")}
                  />
                </div>
                <div className="col-span-full">
                  <TextAreaFormField
                    label="Content*"
                    register={form.register("content")}
                  />
                  {/* Variable Inserter */}
                  <div className="mt-2 flex items-center justify-between">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1">
                          Insert variable
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            const current = form.getValues("content") || "";
                            const token = "{{contact.firstName}}";
                            const next = current
                              ? `${current} ${token}`
                              : token;
                            form.setValue("content", next, {
                              shouldDirty: true,
                            });
                          }}
                        >
                          Contact first name
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            const current = form.getValues("content") || "";
                            const token = "{{contact.lastName}}";
                            const next = current
                              ? `${current} ${token}`
                              : token;
                            form.setValue("content", next, {
                              shouldDirty: true,
                            });
                          }}
                        >
                          Contact last name
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            const current = form.getValues("content") || "";
                            const token = "{{contact.fullName}}";
                            const next = current
                              ? `${current} ${token}`
                              : token;
                            form.setValue("content", next, {
                              shouldDirty: true,
                            });
                          }}
                        >
                          Contact full name
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <MultiselectFormField
                  label="Incident types*"
                  options={incidentTypes.map((type) => ({
                    value: type.id,
                    label: getIncidentTypeLabel(type),
                  }))}
                  register={form.register("incidentTypeIds")}
                />

                <MultiCategorySelectFormField
                  label="Trigger matter status*"
                  items={matterStatusGroups}
                  register={form.register("triggerStatusId")}
                  placeholder="Select status"
                  open={isMatterStatusOpen}
                  setOpen={setIsMatterStatusOpen}
                />
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete scheduled message"
        description={`Are you sure you want to delete "${activeMatterScheduledMessage?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDelete}
      />
    </>
  );
};
